import { Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { CbcReconciliationService } from '../cbc-reconciliation/cbc-reconciliation.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-cbc-delete-variance-data',
    templateUrl: './cbc-delete-variance-data.component.html',
    styleUrls: ['./cbc-delete-variance-data.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CbcDeleteVarianceDataComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  confirmConfig: ConfirmationModalConfig = {
    title: 'Confirm Delete',
    message: 'Are you sure you want to delete?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  filterParams: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};
  rowData: any[] = [];
  deleteLoading = false;

  constructor(
    private cbcService: CbcReconciliationService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = this.resolveSelectedRows(activeScreen);
    this.cdr.markForCheck();
  }

  private resolveSelectedRows(activeScreen: any): any[] {
    const gridData = gridPayloadFromActiveScreen(activeScreen);

    if (Array.isArray(gridData?.batchSelect) && gridData.batchSelect.length) {
      return gridData.batchSelect;
    }
    if (Array.isArray(gridData?.gridData) && gridData.gridData.length) {
      return gridData.gridData;
    }
    if (Array.isArray(gridData) && gridData.length) {
      return gridData;
    }

    const raw = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(raw) && raw.length > 0) {
      return raw;
    }
    if (raw && typeof raw === 'object' && !Array.isArray(raw) && Object.keys(raw).length > 0) {
      return [raw];
    }
    return [];
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private getVarianceIds(): string {
    if (!this.rowData || this.rowData.length === 0) return '';
    const ids = this.rowData
      .map((item) => (item.data?.VARIANCE_KEY != null) ? item.data.VARIANCE_KEY : (item.VARIANCE_KEY != null ? item.VARIANCE_KEY : null))
      .filter((v) => v != null);
    return ids.join(',');
  }

  onConfirm(): void {
    const ids = this.getVarianceIds();
    if (!ids) {
      this.gtwSnackbarService.openSnackBar('No variance data to delete.', 'gtw-snackbar--yellow');
      return;
    }
    this.deleteLoading = true;
    this.confirmConfig = { ...this.confirmConfig, loading: true };
    this.cdr.detectChanges();

    this.cbcService.deleteUserVariance(this.baseUrl, ids, this.filterParams, this.ssoId, this.globalService).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.deleteLoading = false;
        this.confirmConfig = { ...this.confirmConfig, loading: false };
        if (response?.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
          this.cancel();
        } else {
          this.gtwSnackbarService.openSnackBar('Delete successful.', 'gtw-snackbar--green');
          setTimeout(() => {
            const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen;
            if (activeScreen?.screen_key) {
              this.gtwWorkspace.refreshGridData.next(activeScreen);
            }
          }, 500);
        }
        this.cancel();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.deleteLoading = false;
        this.confirmConfig = { ...this.confirmConfig, loading: false };
        this.gtwSnackbarService.openSnackBar(err?.message || 'Error deleting.', 'gtw-snackbar--red');
        this.cancel();
        this.cdr.detectChanges();
      },
    });
  }

  onCancel(): void {
    this.cancel();
  }

  private cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true,
    });
    document
      .querySelector('gtw-cbc-delete-variance-data-ce')
      ?.dispatchEvent(updateEvent);
  }
}
